import React, { useEffect, useState } from 'react';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import { CircularProgress, IconButton } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import { DocumentData } from 'firebase/firestore';
import { enqueueSnackbar } from 'notistack';
import { functions } from '../../../firebase-config';
import { getStorageImageUrl } from '../../api/storage-api/StorageApi';
import { GenericTransaction } from '../../transactions-page/transactions-page-types';
import { getJobDoc } from '../../api/document-api/DocumentAPI';
import { locationMap } from '../../transactions-page/transactions-page-helpers';

// Possible locations for the job
const STORAGE_DIRECTORIES = [
  'invoices',
  'single-utility-invoice',
  'summary-utility-invoice',
];

/**
 * Retrieve the image url for a GenericTransaction job
 * @param jobData data of job
 */
const getTransactionImage = async (jobData: DocumentData) => {
  const sourceData = jobData.sourceData as GenericTransaction;
  const getReceiptsFuncName = locationMap[jobData.location].getReceipts;
  const getReceiptsFunc = httpsCallable(functions, getReceiptsFuncName);
  const resp = await getReceiptsFunc({ transactionId: sourceData.id });
  // we only really need to show the first image (for now)
  const [url] = resp.data as string[];
  return url;
};

/**
 * Attempts to find the image in multiple storage directories
 * @param jobId The ID of the job
 * @returns URL to the image if found, empty string otherwise
 */
const findImageInDirectories = async (jobId: string): Promise<string> => {
  // Create an array of promises, one for each directory check
  const urlPromises = STORAGE_DIRECTORIES.map((directory) => getStorageImageUrl(directory, `${jobId}.pdf`)
    .catch(() => ''));

  // Wait for all promises to resolve
  const urls = await Promise.all(urlPromises);

  // Find the first non-empty URL
  return urls.find((url) => url !== '') || '';
};

/**
 * Retrieves the url for the job image.
 * @param gcsImageUrl existing image url (if the image was upload to GCS)
 * @param jobData data of job containing receipt ids (if there are any)
 */
const getImageUrl = async (
  gcsImageUrl: string,
  jobData: DocumentData | null,
) => gcsImageUrl || (jobData ? await getTransactionImage(jobData) : '');

/**
 * AssociationPdfButton provides a button for users to click during the association section.
 * This button, upon clicking, will retrieve the related invoice images.
 * This allows the user to view the given image pertaining to
 * an association to see if there is an error.
 * @param jobId the id of the job
 * @constructor
 */
function AssociationPdfButton({ jobId } : { jobId: string }) {
  const [jobData, setJobData] = useState<DocumentData | null>(null);
  const [loadingImage, setLoadingImage] = useState<boolean>(false);
  const [gcsImageUrl, setGcsImageUrl] = useState<string>('');

  // retrieves stored url (if there is one) and job data (if there is a doc yet).
  // we do this because some images are uploaded to GCS (by necessity) but images for other jobs (like transactions) are not uploaded to GCS
  useEffect(() => {
    // Check all directories for the image
    findImageInDirectories(jobId)
      .then((url) => setGcsImageUrl(url))
      .catch((err) => console.error('Error finding image:', err));

    // Get job document if available
    getJobDoc(jobId)
      .then((data) => { if (data) setJobData(data); })
      .catch((err) => console.error('Error getting job doc:', err));
  }, [jobId]);

  const jobDataHasImages = !!jobData && !!jobData.sourceData?.receiptIds?.length;
  const hasImage = !!gcsImageUrl || jobDataHasImages;

  // open image url when user clicks button
  const onClick = () => {
    setLoadingImage(true);
    getImageUrl(gcsImageUrl, jobData)
      .then((url) => window.open(url))
      .catch(() => enqueueSnackbar('Failed to get image', { variant: 'error' }))
      .finally(() => setLoadingImage(false));
  };

  const icon = hasImage ? <ImageSearchIcon /> : <ImageNotSupportedIcon />;

  return (
    <div className="mr-2">
      <IconButton
        disabled={!hasImage}
        onClick={onClick}
      >
        {loadingImage ? <CircularProgress className="my-auto" size={20} /> : icon}
      </IconButton>
    </div>
  );
}

export default AssociationPdfButton;
